import {
  Injectable,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  isSectionStructureObject,
  isSubSectionStructureObject,
  isWidgetStructureObject,
  SectionStructureObject,
  SubSectionStructureObject,
  WidgetStructureObject,
} from '../../api/interfaces';
import { UniqueIdService } from '../../services/builder/unique-id.service';
import { BuilderService } from '../../services/builder/builder.service';
import { cloneDeep } from 'lodash-es';

@Injectable()
export class ClipboardService {
  private builderService = inject(BuilderService);
  private uniqueIdService = inject(UniqueIdService);

  readonly clipBoard: WritableSignal<
    | SectionStructureObject
    | SubSectionStructureObject
    | WidgetStructureObject
    | undefined
  > = signal(undefined);

  isSectionStructure = computed(() =>
    isSectionStructureObject(this.clipBoard()),
  );

  isSubSectionStructure = computed(() =>
    isSubSectionStructureObject(this.clipBoard()),
  );
  isWidgetStructure = computed(() => isWidgetStructureObject(this.clipBoard()));

  copy(
    data:
      | SectionStructureObject
      | SubSectionStructureObject
      | WidgetStructureObject,
  ) {
    this.clipBoard.set(data);
  }

  paste(fatherId: 'root' | string) {
    if (!this.clipBoard()) return;

    if (this.isSectionStructure()) {
      this.pasteSectionBlock();
    } else if (this.isSubSectionStructure()) {
      this.pasteSubSectionBlock(fatherId);
    } else {
      this.pasteWidgetBlock(fatherId);
    }
  }

  private pasteSectionBlock() {
    const block: Record<
      string,
      SectionStructureObject | SubSectionStructureObject | WidgetStructureObject
    > = {};

    const copiedSection = cloneDeep(this.clipBoard()) as SectionStructureObject;
    const copiedSectionChildren = copiedSection.children;

    const records = copiedSectionChildren.map((subSectionId) => {
      const subsection = cloneDeep(
        this.builderService.moduleStructure()[
          subSectionId
        ] as SubSectionStructureObject,
      );
      const widgets = subsection.children.map((widgetId) => {
        return cloneDeep(
          this.builderService.moduleStructure()[widgetId],
        ) as WidgetStructureObject;
      });

      return {
        subsection,
        widgets,
      };
    });

    copiedSection.children = [];

    records.forEach(({ subsection, widgets }) => {
      const newSubSectionId = this.uniqueIdService.getUniqueId(
        subsection._type,
        Object.keys(block),
      );
      copiedSection.children.push(newSubSectionId);
      subsection.children = [];

      widgets.forEach((widget) => {
        const newWidgetId = this.uniqueIdService.getUniqueId(
          widget._type,
          Object.keys(block),
        );
        subsection.children.push(newWidgetId);
        block[newWidgetId] = { ...widget };
      });

      block[newSubSectionId] = { ...subsection };
    });

    block[
      this.uniqueIdService.getUniqueId(copiedSection._type, Object.keys(block))
    ] = {
      ...copiedSection,
    };
    this.builderService.addBlock(block, 'root');
  }

  private pasteSubSectionBlock(fatherId: string) {
    const block: Record<
      string,
      SubSectionStructureObject | WidgetStructureObject
    > = {};

    const copiedSubSection = cloneDeep(
      this.clipBoard(),
    ) as SubSectionStructureObject;
    const copiedSubSectionChildren = copiedSubSection.children;

    const widgets = copiedSubSectionChildren.map((widgetId) => {
      return cloneDeep(
        this.builderService.moduleStructure()[widgetId],
      ) as WidgetStructureObject;
    });

    copiedSubSection.children = [];
    widgets.forEach((widget) => {
      const newWidgetId = this.uniqueIdService.getUniqueId(
        widget._type,
        Object.keys(block),
      );
      copiedSubSection.children.push(newWidgetId);
      block[newWidgetId] = { ...widget };
    });

    block[
      this.uniqueIdService.getUniqueId(
        copiedSubSection._type,
        Object.keys(block),
      )
    ] = {
      ...copiedSubSection,
    };
    this.builderService.addBlock(block, fatherId);
  }

  private pasteWidgetBlock(fatherId: string) {
    const copiedWidget = cloneDeep(this.clipBoard()) as WidgetStructureObject;
    const block: Record<string, WidgetStructureObject> = {};
    block[
      this.uniqueIdService.getUniqueId(copiedWidget._type, Object.keys(block))
    ] = copiedWidget;
    this.builderService.addBlock(block, fatherId);
  }
}
